import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { api, getToken, setToken, setUnauthorizedHandler } from '../api/client';

// AuthContext holds the authenticated user and login/logout actions. The bearer
// token lives in localStorage (see api/client); this context mirrors the
// derived user object and gates the app.
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // Console areas granted by the account's policies (see /auth/me meta).
  // Superadmins come back holding all of them.
  const [features, setFeatures] = useState([]);
  // Whether any provider service is granted, which is what makes the service
  // catalogue worth offering. Separate from `features`: a service is not a
  // console area, and the answer comes from the scoped catalogue read rather
  // than from a policy id.
  const [hasServices, setHasServices] = useState(false);
  const [loading, setLoading] = useState(true);

  // If the API rejects our token mid-session, drop the user so the route guard
  // sends us back to /login.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
      setFeatures([]);
      setHasServices(false);
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  // On first load, validate any stored token by fetching the current user.
  useEffect(() => {
    if (!getToken()) {
      setLoading(false);
      return;
    }
    api
      .getMe()
      .then((res) => {
        setUser(res?.data || null);
        setFeatures(res?.meta?.features || []);
        setHasServices(!!res?.meta?.hasServices);
      })
      .catch(() => {
        setToken(null);
        setUser(null);
        setFeatures([]);
        setHasServices(false);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await api.login({ email, password });
    setToken(res.token);
    // The login response carries no grants, so read them once here rather than
    // leaving the shell with an empty feature set until the next reload. The
    // user is published last, on purpose: authenticating is what releases the
    // redirect off /login, and `landing` below is read from the grants — a
    // user-first order would pick the landing page from an empty set.
    try {
      const me = await api.getMe();
      setFeatures(me?.meta?.features || []);
      setHasServices(!!me?.meta?.hasServices);
    } catch {
      setFeatures([]);
      setHasServices(false);
    }
    setUser(res.user);
    return res.user;
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setFeatures([]);
    setHasServices(false);
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      login,
      logout,
      isAuthenticated: !!user,
      isAdmin: user?.role === 'superadmin',
      features,
      // A superadmin always sees the whole catalogue, so the flag is implied.
      hasServices: user?.role === 'superadmin' || hasServices,
      // allows() mirrors the server-side policy check so the shell can hide
      // what it cannot reach; the API enforces it regardless.
      allows: (feature) => user?.role === 'superadmin' || features.includes(feature),
      // Where a sign-in lands when nothing else was requested. A member comes
      // to the console for their bill, so Billing is theirs; a superadmin's
      // landing stays Console home, since Billing deliberately carries no buy
      // side and is not the page they came for. A member granted neither falls
      // back to the service catalogue, which is ungated — landing anyone on an
      // area their policy does not grant would show a page whose every request
      // 403s, which is the same reason Console home has a route guard.
      landing:
        user?.role === 'superadmin'
          ? '/home'
          : features.includes('billing')
            ? '/billing'
            : '/services',
    }),
    [user, loading, login, logout, features, hasServices]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
